import {FC, useCallback, useEffect, useState} from 'react'
import {useHistory, useLocation} from 'react-router-dom'
import {CustomAlert} from '../../../../shared/components/CustomAlert'
import '../../style/application.scss'
import DooraHelperModal from './DooraHelperModal'
import DooraCardSignInModal from './DooraCardSignInModal'

const trustItems = [
  {
    label: 'Encrypted',
    color: '#F0A9D8',
    icon: (
      <svg width='20' height='20' viewBox='0 0 20 20' fill='none' aria-hidden='true'>
        <path
          d='M10 2.5l6 2v5c0 3.8-2.6 7.2-6 8.5-3.4-1.3-6-4.7-6-8.5v-5l6-2z'
          stroke='currentColor'
          strokeWidth='1.6'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </svg>
    ),
  },
  {
    label: 'Never sold',
    color: '#53BCFF',
    icon: (
      <svg width='20' height='20' viewBox='0 0 20 20' fill='none' aria-hidden='true'>
        <circle
          cx='10'
          cy='10'
          r='7'
          stroke='currentColor'
          strokeWidth='1.6'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
        <path
          d='M12 7.3c-.5-.8-1.3-1.3-2.3-1.3-1.3 0-2.2.8-2.2 1.8 0 1 .9 1.5 2.2 1.9 1.5.4 2.6.9 2.6 2.1 0 1.1-1 2-2.5 2-1 0-1.9-.4-2.4-1.2M10 4.8v1.4M10 13.8v1.4'
          stroke='currentColor'
          strokeWidth='1.6'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
        <path
          d='M5 15L15 5'
          stroke='currentColor'
          strokeWidth='1.9'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </svg>
    ),
  },
  {
    label: 'You delete it',
    color: '#BB9AE4',
    icon: (
      <svg width='20' height='20' viewBox='0 0 20 20' fill='none' aria-hidden='true'>
        <path
          d='M4 5.5h12M8 5.5V4a1.5 1.5 0 011.5-1.5h1A1.5 1.5 0 0112 4v1.5M5.5 5.5l.8 10A1.5 1.5 0 007.8 17h4.4a1.5 1.5 0 001.5-1.5l.8-10'
          stroke='currentColor'
          strokeWidth='1.6'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </svg>
    ),
  },
]

const ApplicationLandingPage: FC = () => {
  const history = useHistory()
  const location = useLocation<{cardDeleted?: boolean}>()
  const [isSignInOpen, setIsSignInOpen] = useState(false)
  const [isHelperOpen, setIsHelperOpen] = useState(false)

  const dismissDeletionAlert = useCallback(() => {
    history.replace({...location, state: {...location.state, cardDeleted: undefined}})
  }, [history, location])

  useEffect(() => {
    if (location.state?.cardDeleted !== true) return

    const timeout = window.setTimeout(dismissDeletionAlert, 6000)
    return () => window.clearTimeout(timeout)
  }, [location.state?.cardDeleted, dismissDeletionAlert])

  const handleSignInSuccess = (sessionId: string) => {
    setIsSignInOpen(false)
    history.replace(`/my-doora-card?sessionId=${encodeURIComponent(sessionId)}`)
  }

  return (
    <div className='application-landing-page'>
      <div className='application-landing-bg' aria-hidden='true'>
        <span className='application-landing-orb application-landing-orb-left' />
        <span className='application-landing-orb application-landing-orb-center' />
        <span className='application-landing-orb application-landing-orb-right' />
      </div>
      <button
        type='button'
        className='btn btn-link application-landing-top-signin'
        onClick={() => setIsSignInOpen(true)}
      >
        Sign in <span aria-hidden='true'>&rarr;</span>
      </button>

      <main className='application-landing-content'>
        {location.state?.cardDeleted === true && (
          <div role='status'>
            <CustomAlert
              type='success'
              className='application-landing-deletion-alert'
              message='Your DOORA Card has been permanently deleted.'
              closeToaster={dismissDeletionAlert}
            />
          </div>
        )}
        <img src='/media/logos/doora-logo.png' alt='Doora' className='application-landing-logo' />

        <h1 className='application-landing-title'>Your rental application</h1>
        <p className='application-landing-subtitle'>Your data. Your choice.</p>

        <ul className='application-landing-feature-list'>
          {trustItems.map((item) => (
            <li key={item.label} className='application-landing-feature-item trust-item'>
              <span
                className='application-landing-feature-icon trust-icon'
                style={{color: item.color}}
                aria-hidden='true'
              >
                {item.icon}
              </span>
              <span className='application-landing-feature-label lbl'>{item.label}</span>
            </li>
          ))}
        </ul>

        <p className='application-landing-note'>You control your information.</p>

        <button
          className='btn application-landing-cta'
          onClick={() => history.push('/application-wizard')}
        >
          Get started
        </button>

        <button
          type='button'
          className='btn btn-link application-landing-help-link'
          onClick={() => setIsHelperOpen(true)}
        >
          Questions? Talk to a real person <span aria-hidden='true'>&rarr;</span>
        </button>
      </main>
      {isSignInOpen && (
        <DooraCardSignInModal
          onClose={() => setIsSignInOpen(false)}
          onSuccess={handleSignInSuccess}
        />
      )}
      {isHelperOpen && <DooraHelperModal onClose={() => setIsHelperOpen(false)} />}
    </div>
  )
}

export default ApplicationLandingPage
